<script setup lang="ts">
  import { onMounted, useTemplateRef } from 'vue'
  import {
    DataList,
    DataListItem,
    DataListItemTitle,
    Button
  } from '@gat-ship/vue-components'
  import {
    IconEstimatedTime,
    IconLoading,
    IconPlus,
    IconTimeOfDay
  } from '@gat-ship/icons'
  import type { EstimatedTimeName } from '@gat-ship/types'
  import { useEstimatedTimeNames } from '../composables/useEstimatedTimeNames'
  import EstimatedTimeNameModalAdd from './providers/estimated-time-names-provider/modals/EstimatedTimeNameModalAdd.vue'
  import EstimatedTimeNameModalEdit from './providers/estimated-time-names-provider/modals/EstimatedTimeNameModalEdit.vue'

  const service = useEstimatedTimeNames()

  const addModal =
    useTemplateRef<InstanceType<typeof EstimatedTimeNameModalAdd>>('addModal')
  const editModal =
    useTemplateRef<InstanceType<typeof EstimatedTimeNameModalEdit>>('editModal')

  onMounted(() => {
    void service.fetch()
  })

  function openAdd() {
    addModal.value?.showModal()
  }

  function openEdit(entry: EstimatedTimeName) {
    editModal.value?.showModal({ entry })
  }

  // Hosts (route view, administration modal panel) render their own "Add"
  // affordance in their chrome and forward it here.
  defineExpose({ openAdd })
</script>

<template>
  <div
    v-if="service.isLoading.estimatedTimeNames"
    class="text-muted-foreground mx-auto flex animate-pulse items-center justify-center gap-2 p-6"
  >
    <IconLoading class="size-6" /> Loading…
  </div>

  <div
    v-else-if="service.estimatedTimeNames.value.length === 0"
    class="text-muted-foreground mx-auto flex flex-col items-center gap-2 p-10 text-center"
  >
    <IconEstimatedTime class="size-8 opacity-60" />
    <div class="text-sm">No estimated time names yet.</div>
    <Button size="lg" @click="openAdd">
      <IconPlus class="size-4" />
      Add time name
    </Button>
  </div>

  <DataList v-else>
    <DataListItem
      v-for="entry in service.estimatedTimeNames.value"
      :key="entry.id"
      @click:item="openEdit(entry)"
    >
      <div class="flex w-full items-center gap-3">
        <IconTimeOfDay
          :time="entry.referenceTime"
          class="text-muted-foreground size-4 shrink-0"
        />
        <DataListItemTitle>
          <div class="flex items-baseline gap-2 text-sm">
            <span class="font-medium">{{ entry.code }}</span>
            <span class="text-muted-foreground font-normal"
              >— {{ entry.name }}</span
            >
          </div>
        </DataListItemTitle>
        <div class="ml-auto flex items-center gap-3">
          <span class="text-muted-foreground font-mono text-xs">
            {{ entry.referenceTime }}
          </span>
          <span
            v-if="entry.isInactive"
            class="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs"
          >
            Inactive
          </span>
        </div>
      </div>
    </DataListItem>
  </DataList>

  <EstimatedTimeNameModalAdd ref="addModal" />
  <EstimatedTimeNameModalEdit ref="editModal" />
</template>
